import React, { useState, useEffect } from "react";
import "./Prescription.css";
import { useHistory, useParams } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import Cookies from "js-cookie";
import {
  Form,
  Input,
  InputNumber,
  Button,
  notification,
  Card,
  Tag,
  Select,
  Table,
  Spin
} from "antd";
import {
  HiOutlineChevronLeft,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineClock,
  HiOutlineDocumentText
} from 'react-icons/hi';
import moment from "moment";
import { encryptData, decryptData } from '../Crypto_Helpers/cryptoHelper';
import { initEncryptionSession } from '../../encryptionSession';

// Icons for Patient
import MaleIcon from "../../assets/Doctor_Images/male_user.png";
import FemaleIcon from "../../assets/Doctor_Images/female_user.png";

const { Option } = Select;

const Prescription = () => {
  const history = useHistory();
  const { patientId, bookingId, date } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const [medicineOptions, setMedicineOptions] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [selectedMedicineData, setSelectedMedicineData] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);

  // Watch dosage fields to trigger re-renders for button states
  const dosageWatch = {
    morning: Form.useWatch('morning', form),
    afternoon: Form.useWatch('afternoon', form),
    evening: Form.useWatch('evening', form),
    night: Form.useWatch('night', form),
  };

  useEffect(() => {
    fetchAppointmentDetails();
    fetchMedicineOptions();
    fetchExistingPrescription();
  }, []);

  const fetchAppointmentDetails = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post("doctor/getAppointmentList", {
        clinic_id: Cookies.get("Clinic_id") || Cookies.get("clinic_id"),
        doctor_id: Cookies.get("Doctor_id") || Cookies.get("doctor_id"),
        patient_type: "",
        period: "day",
        search_date: date,
        search_date_to: date
      });

      if (response.data.data && response.data.data[0]) {
        const decryptedData = response.data.data[0].result;
        if (Array.isArray(decryptedData)) {
          const filtered = decryptedData.find(x => x.patient_id == patientId && x.booking_id == bookingId);
          if (filtered) setAppointment(filtered);
        }
      }
    } catch (err) {
      console.error("Failed to fetch appointment details", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingPrescription = async () => {
    try {
      const response = await axiosInstance.post("doctor/getPrescriptionImage", {
        doctorId: Cookies.get("Doctor_id") || Cookies.get("doctor_id"),
        patientId: patientId,
        booking_id: bookingId,
      });

      const records = response.data.data || response.data;
      if (Array.isArray(records)) {
        const record = records[0];
        if (record && record.p_medicinedetails) {
          try {
            const parsed = typeof record.p_medicinedetails === 'string'
              ? JSON.parse(record.p_medicinedetails)
              : record.p_medicinedetails;
            setPrescriptions(Array.isArray(parsed) ? parsed : []);
          } catch (e) {
            console.error("Prescription Parse Error", e);
          }
        }
      }
    } catch (err) {
      console.error("Failed to fetch existing prescription", err);
    }
  };

  const fetchMedicineOptions = async () => {
    try {
      const pharmacyId = Cookies.get("pharmacy_id") || localStorage.getItem("pharmacy_id") || "";
      const clinicId = Cookies.get("Clinic_id") || Cookies.get("clinic_id") || "";
      const doctorId = Cookies.get("Doctor_id") || Cookies.get("doctor_id") || "";

      const response = await axiosInstance.post("doctor/get_mas_pharm_medicine_rate", {
        pharmacy_id: pharmacyId,
        clinic_id: clinicId,
        doctor_id: doctorId,
        pageNumber: 0,
        pageSize: 0,
      });

      const rawData = response.data.data || response.data;
      if (Array.isArray(rawData)) {
        setMedicineOptions(rawData);
        if (!pharmacyId && rawData.length > 0) {
          const resolvedPharmId = rawData[0].Pharmacy_id || rawData[0].pharmacy_id;
          if (resolvedPharmId) {
            Cookies.set("pharmacy_id", resolvedPharmId, { expires: 1 });
            localStorage.setItem("pharmacy_id", resolvedPharmId);
          }
        }
      }
    } catch (err) {
      console.error("Failed to fetch medicines", err);
    }
  };

  const handleAddMedicine = (values) => {
    if (!selectedMedicineData) {
      notification.warning({ message: "Please select a medicine first" });
      return;
    }

    const counts = [
      values.morning ? 1 : 0,
      values.afternoon ? 1 : 0,
      values.evening ? 1 : 0,
      values.night ? 1 : 0
    ];

    if (counts.every(c => c === 0)) {
      notification.warning({ message: "Please select at least one dosage time (M-A-E-N)" });
      return;
    }

    const totalMedicine = counts.reduce((a, b) => a + b, 0) * (values.days || 1);
    const price = (selectedMedicineData.per_tablet_price || 0) * totalMedicine;

    const newMedicine = {
      id: selectedMedicineData.id || selectedMedicineData.value,
      name: selectedMedicineData.medicine_name || selectedMedicineData.label,
      pharmacy_id: selectedMedicineData.Pharmacy_id || selectedMedicineData.pharmacy_id || Cookies.get("pharmacy_id") || localStorage.getItem("pharmacy_id") || 0,
      counts: counts,
      days: values.days || 1,
      foodtype: values.foodtype,
      per_tablet_price: selectedMedicineData.per_tablet_price,
      totalmedicine: totalMedicine,
      price: price,
      total_count: selectedMedicineData.total_count,
      stock: selectedMedicineData.stock
    };

    if (editingIndex !== null) {
      const updated = [...prescriptions];
      updated[editingIndex] = newMedicine;
      setPrescriptions(updated);
      setEditingIndex(null);
      notification.success({ message: "Medicine updated in list" });
    } else {
      setPrescriptions([...prescriptions, newMedicine]);
      notification.success({ message: "Medicine added to list" });
    }

    form.resetFields(['morning', 'afternoon', 'evening', 'night', 'medicine_id', 'days', 'foodtype']);
    form.setFieldsValue({ days: 1, foodtype: 1 });
    setSelectedMedicineData(null);
  };

  const handleEditRow = (record, index) => {
    setEditingIndex(index);
    setSelectedMedicineData({
      id: record.id,
      medicine_name: record.name,
      per_tablet_price: record.per_tablet_price,
      total_count: record.total_count,
      stock: record.stock
    });

    form.setFieldsValue({
      medicine_id: record.id,
      morning: record.counts[0] === 1,
      afternoon: record.counts[1] === 1,
      evening: record.counts[2] === 1,
      night: record.counts[3] === 1,
      days: record.days,
      foodtype: record.foodtype
    });

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const removeMedicine = (index) => {
    const updated = [...prescriptions];
    updated.splice(index, 1);
    setPrescriptions(updated);
  };

  const onFinish = async () => {
    if (prescriptions.length === 0) {
      notification.error({ message: "Please add at least one medicine" });
      return;
    }

    setLoading(true);
    try {
      const targetPharmacyId = Cookies.get("pharmacy_id") || localStorage.getItem("pharmacy_id") || (medicineOptions.length > 0 ? (medicineOptions[0].Pharmacy_id || medicineOptions[0].pharmacy_id) : 0);

      const payload = {
        doctor_id: Cookies.get("Doctor_id") || Cookies.get("doctor_id"),
        patient_id: patientId,
        medicines: "",
        file_name: "",
        file_path: "",
        is_send_to_pharam: 1,
        booking_id: bookingId,
        pharm_id: targetPharmacyId || 0,
        clinic_id: Cookies.get("Clinic_id") || Cookies.get("clinic_id") || 0,
        is_walkin: "1",
        medicinedetails: prescriptions,
        appointmentDate: date
      };

      const response = await axiosInstance.post("doctor/insertPrescription", payload);

      if (response.data.status === 1) {
        notification.success({
          message: "Prescription Saved",
          description: "Patient medicine list sent to pharmacy."
        });
        history.push(`/dashboard/appointments/report/${patientId}/${bookingId}/${date}`);
      } else {
        notification.error({ message: response.data.msg || "Failed to save prescription" });
      }
    } catch (err) {
      console.error("Save error", err);
      notification.error({ message: "An error occurred while saving" });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Medicine Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <span style={{ fontWeight: 700, color: '#1a202c', fontSize: 14 }}>{text}</span>
    },
    {
      title: 'Dose (M-A-E-N)',
      key: 'dosage',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 6 }}>
          {record.counts.map((c, i) => (
            <div
              key={i}
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 900,
                background: c > 0 ? '#0D9488' : '#F1F5F9',
                color: c > 0 ? '#fff' : '#CBD5E1',
                boxShadow: c > 0 ? '0 2px 8px rgba(13,148,136,0.18)' : 'none',
                transition: 'all 0.2s',
              }}
            >
              {i === 0 ? 'M' : i === 1 ? 'A' : i === 2 ? 'E' : 'N'}
            </div>
          ))}
        </div>
      )
    },
    {
      title: 'Duration',
      key: 'duration',
      render: (_, record) => (
        <span style={{ color: '#4B5563', fontWeight: 600, fontSize: 14 }}>{record.days} Days</span>
      )
    },
    {
      title: 'Food Instruction',
      key: 'food',
      render: (_, record) => (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '5px 14px',
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: '0.04em',
          background: record.foodtype === 0 ? '#FEF3C7' : '#E0F2F1',
          color: record.foodtype === 0 ? '#D97706' : '#0D9488',
        }}>
          {record.foodtype === 0 ? 'Before Food' : 'After Food'}
        </div>
      )
    },
    {
      title: 'Qty',
      key: 'qty',
      align: 'center',
      render: (_, record) => (
        <span style={{ fontWeight: 900, color: '#0D9488', fontSize: 16 }}>{record.totalmedicine}</span>
      )
    },
    {
      title: 'Action',
      key: 'action',
      align: 'right',
      render: (_, record, index) => (
        <div style={{ display: 'flex', gap: 14, justifyContent: 'flex-end' }}>
          <button
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0D9488', padding: 4, transition: 'transform 0.2s' }}
            onClick={() => handleEditRow(record, index)}
            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.15)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          >
            <HiOutlineDocumentText size={20} />
          </button>
          <button
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: 4, transition: 'transform 0.2s' }}
            onClick={() => removeMedicine(index)}
            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.15)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          >
            <HiOutlineTrash size={20} />
          </button>
        </div>
      )
    }
  ];

  if (loading && !appointment) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f0fdf9' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="rx-page-root">
      {/* ── Header ── */}
      <div className="rx-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <button className="rx-back-btn" onClick={() => history.goBack()}>
            <HiOutlineChevronLeft size={22} />
          </button>
          <div>
            <h2 className="rx-title">E-Prescription</h2>
            <p className="rx-subtitle">Digital Medication Management</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <div className="rx-badge rx-badge-teal">
            <span className="rx-badge-label">Booking ID</span>
            <span className="rx-badge-value">#{bookingId}</span>
          </div>
          <div className="rx-badge rx-badge-amber">
            <span className="rx-badge-label">Visit Date</span>
            <span className="rx-badge-value">{moment(date).format("DD MMM YYYY")}</span>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="rx-body">

        {/* ── LEFT COLUMN ── */}
        <div className="rx-left">

          {/* Patient Card */}
          {appointment && (
            <div className="rx-patient-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div className="rx-avatar-wrap">
                  <img
                    src={appointment.gender?.toLowerCase().startsWith('m') ? MaleIcon : FemaleIcon}
                    alt="patient"
                    className="rx-avatar"
                  />
                </div>
                <div>
                  <h3 className="rx-patient-name">{appointment.name}</h3>
                  <p className="rx-patient-meta">{appointment.gender}, {appointment.age} Yrs</p>
                </div>
              </div>
              <div className="rx-patient-grid">
                <div className="rx-patient-info-box">
                  <span className="rx-info-label">Patient ID</span>
                  <span className="rx-info-value">ID000{appointment.patient_id}</span>
                </div>
                <div className="rx-patient-info-box">
                  <span className="rx-info-label">Clinic</span>
                  <span className="rx-info-value" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {appointment.clinicName || "Shiva Clinics"}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Prescribe Form Card */}
          <div className="rx-form-card">
            <div className="rx-form-card-header">
              <span className="rx-form-card-dot" />
              Prescribe Medicine
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleAddMedicine}
              initialValues={{ days: 1, foodtype: 1 }}
              style={{ padding: '0 20px 20px' }}
            >
              {/* Medicine Select */}
              <Form.Item
                name="medicine_id"
                label={<span className="rx-field-label"><span style={{ color: '#ef4444', marginRight: 4 }}>*</span>Select Medicine</span>}
                rules={[{ required: true, message: 'Select medicine' }]}
                style={{ marginBottom: 16 }}
              >
                <Select
                  showSearch
                  placeholder="Select Medicine"
                  optionFilterProp="children"
                  onSelect={(val, option) => setSelectedMedicineData(option.data)}
                  className="rx-select"
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {medicineOptions.map((m, index) => (
                    <Option key={index} value={m.id} label={m.medicine_name} data={m}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                        <span style={{ fontWeight: 700, color: '#374151' }}>{m.medicine_name}</span>
                        <Tag color="blue" style={{ margin: 0, border: 'none', fontWeight: 900, fontSize: 9 }}>Stock: {m.stock}</Tag>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {/* Daily Schedule */}
              <div style={{ marginBottom: 16 }}>
                <label className="rx-field-label" style={{ display: 'block', marginBottom: 10 }}>
                  Daily Schedule (Morn - Noon - Eve - Night)
                </label>
                <div className="rx-dosage-grid">
                  {[
                    { name: 'morning', label: 'Morn', icon: 'M' },
                    { name: 'afternoon', label: 'Noon', icon: 'A' },
                    { name: 'evening', label: 'Eve', icon: 'E' },
                    { name: 'night', label: 'Night', icon: 'N' }
                  ].map(item => {
                    const active = dosageWatch[item.name];
                    return (
                      <Form.Item name={item.name} valuePropName="checked" style={{ margin: 0 }} key={item.name}>
                        <button
                          type="button"
                          onClick={() => {
                            const val = form.getFieldValue(item.name);
                            form.setFieldsValue({ [item.name]: !val });
                          }}
                          className={`rx-dose-btn ${active ? 'rx-dose-btn--active' : ''}`}
                        >
                          <span className="rx-dose-icon">{item.icon}</span>
                          <span className="rx-dose-label">{item.label}</span>
                        </button>
                      </Form.Item>
                    );
                  })}
                </div>
              </div>

              {/* Days & Timing */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
                <Form.Item
                  label={<span className="rx-field-label">Days</span>}
                  name="days"
                  style={{ margin: 0 }}
                >
                  <InputNumber min={1} className="rx-input-number" />
                </Form.Item>
                <Form.Item
                  label={<span className="rx-field-label">Timing</span>}
                  name="foodtype"
                  style={{ margin: 0 }}
                >
                  <Select className="rx-select">
                    <Option value={0}>Before Food</Option>
                    <Option value={1}>After Food</Option>
                  </Select>
                </Form.Item>
              </div>

              {/* Submit */}
              <button
                type="submit"
                className={`rx-add-btn ${editingIndex !== null ? 'rx-add-btn--edit' : ''}`}
              >
                {editingIndex !== null ? 'Update Medication' : '+ Add Medicine to List'}
              </button>

              {editingIndex !== null && (
                <button
                  type="button"
                  className="rx-cancel-edit-btn"
                  onClick={() => {
                    setEditingIndex(null);
                    form.resetFields();
                    form.setFieldsValue({ days: 1, foodtype: 1 });
                    setSelectedMedicineData(null);
                  }}
                >
                  Cancel Edit
                </button>
              )}
            </Form>
          </div>
        </div>

        {/* ── RIGHT COLUMN ── */}
        <div className="rx-right">
          <div className="rx-table-card">
            <div className="rx-table-card-header">
              <span style={{ fontWeight: 800, fontSize: 17, color: '#111827' }}>
                Medication List <span style={{ color: '#6B7280' }}>( {prescriptions.length} )</span>
              </span>
            </div>

            <Table
              columns={columns}
              dataSource={prescriptions}
              pagination={false}
              rowKey={(record, index) => index}
              className="rx-table"
              locale={{
                emptyText: (
                  <div style={{ padding: '80px 0', textAlign: 'center' }}>
                    <div style={{
                      width: 80, height: 80, borderRadius: '50%',
                      background: '#F8FAFC', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 16px', color: '#E2E8F0'
                    }}>
                      <HiOutlineDocumentText size={40} />
                    </div>
                    <p style={{ color: '#9CA3AF', fontWeight: 700, margin: 0 }}>No medications specified.</p>
                  </div>
                )
              }}
            />

            {prescriptions.length > 0 && (
              <div className="rx-footer-bar">
                <div>
                  <p className="rx-total-label">Total Quantity</p>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span className="rx-total-value">
                      {prescriptions.reduce((sum, item) => sum + item.totalmedicine, 0)}
                    </span>
                    <span style={{ fontSize: 12, color: '#94A3B8', fontWeight: 600 }}>Units</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button className="rx-btn-cancel" onClick={() => history.goBack()}>
                    Cancel
                  </button>
                  <button
                    className="rx-btn-send"
                    onClick={onFinish}
                    disabled={loading}
                  >
                    {loading ? <Spin size="small" style={{ marginRight: 8 }} /> : null}
                    Send to Pharmacy
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Global Styles ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&display=swap');

        * { box-sizing: border-box; }

        .rx-page-root {
          font-family: 'Outfit', sans-serif;
          background: #f5f7fa;
          min-height: 100vh;
          padding: 24px;
          max-width: 1600px;
          margin: 0 auto;
        }

        /* Header */
        .rx-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #fff;
          padding: 14px 28px;
          border-radius: 20px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.05);
          margin-bottom: 22px;
        }
        .rx-back-btn {
          background: none;
          border: none;
          cursor: pointer;
          color: #0D9488;
          padding: 6px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          transition: background 0.18s;
        }
        .rx-back-btn:hover { background: #f0fdf9; }
        .rx-title {
          font-size: 22px;
          font-weight: 900;
          color: #111827;
          margin: 0;
          line-height: 1.2;
        }
        .rx-subtitle {
          font-size: 13px;
          color: #6B7280;
          font-weight: 500;
          margin: 0;
        }
        .rx-badge {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 8px 22px;
          border-radius: 16px;
          min-width: 110px;
        }
        .rx-badge-teal {
          background: #E6F6F4;
          border: 1.5px solid #D1EEEC;
        }
        .rx-badge-amber {
          background: #FFFBEB;
          border: 1.5px solid #FEF3C7;
        }
        .rx-badge-label {
          font-size: 9px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          margin-bottom: 2px;
        }
        .rx-badge-teal .rx-badge-label { color: #0D9488; }
        .rx-badge-amber .rx-badge-label { color: #D97706; }
        .rx-badge-value {
          font-size: 17px;
          font-weight: 900;
          color: #111827;
        }

        /* Body layout */
        .rx-body {
          display: grid;
          grid-template-columns: 380px 1fr;
          gap: 22px;
          align-items: start;
        }
        @media (max-width: 960px) {
          .rx-body { grid-template-columns: 1fr; }
        }

        /* Left */
        .rx-left { display: flex; flex-direction: column; gap: 18px; }

        /* Patient Card */
        .rx-patient-card {
          background: linear-gradient(135deg, #0D9488 0%, #0a7c73 100%);
          border-radius: 22px;
          padding: 22px;
          box-shadow: 0 8px 28px rgba(13,148,136,0.22);
        }
        .rx-avatar-wrap {
          background: rgba(255,255,255,0.15);
          border: 2px solid rgba(255,255,255,0.25);
          border-radius: 16px;
          padding: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .rx-avatar {
          width: 52px;
          height: 52px;
          border-radius: 10px;
          object-fit: cover;
          display: block;
        }
        .rx-patient-name {
          font-size: 18px;
          font-weight: 900;
          color: #fff;
          margin: 0;
          line-height: 1.2;
        }
        .rx-patient-meta {
          color: rgba(255,255,255,0.7);
          font-size: 12px;
          font-weight: 600;
          margin: 0;
        }
        .rx-patient-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-top: 16px;
        }
        .rx-patient-info-box {
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 14px;
          padding: 10px 14px;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .rx-info-label {
          font-size: 8.5px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: rgba(255,255,255,0.5);
          margin-bottom: 3px;
        }
        .rx-info-value {
          font-size: 13px;
          font-weight: 800;
          color: #fff;
        }

        /* Form card */
        .rx-form-card {
          background: #fff;
          border-radius: 20px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.05);
          overflow: hidden;
        }
        .rx-form-card-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 16px 22px;
          font-weight: 900;
          font-size: 15px;
          color: #1a202c;
          border-bottom: 1.5px solid #F3F4F6;
        }
        .rx-form-card-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #0D9488;
          flex-shrink: 0;
        }
        .rx-field-label {
          font-size: 11px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.07em;
          color: #374151;
        }

        /* Dosage buttons */
        .rx-dosage-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 10px;
        }
        .rx-dose-btn {
          aspect-ratio: 1;
          width: 100%;
          border: none;
          border-radius: 18px;
          background: #F8FAFC;
          color: #CBD5E1;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          transition: all 0.25s cubic-bezier(0.34,1.56,0.64,1);
          padding: 0;
        }
        .rx-dose-btn--active {
          background: #0D9488;
          color: #fff;
          box-shadow: 0 6px 18px rgba(13,148,136,0.32);
          transform: scale(1.04);
        }
        .rx-dose-icon {
          font-size: 17px;
          font-weight: 900;
          line-height: 1;
        }
        .rx-dose-label {
          font-size: 7.5px;
          text-transform: uppercase;
          font-weight: 900;
          margin-top: 5px;
          opacity: 0.55;
          letter-spacing: 0.06em;
        }
        .rx-dose-btn--active .rx-dose-label { opacity: 0.8; }

        /* Ant overrides inside form */
        .rx-select .ant-select-selector {
          height: 46px !important;
          border-radius: 14px !important;
          border-color: #e5e7eb !important;
          display: flex !important;
          align-items: center !important;
          font-weight: 600 !important;
        }
        .rx-input-number {
          width: 100% !important;
          height: 46px !important;
          border-radius: 14px !important;
          border-color: #e5e7eb !important;
          font-weight: 600 !important;
          display: flex;
          align-items: center;
        }
        .rx-input-number .ant-input-number-input {
          height: 44px !important;
          font-weight: 700 !important;
        }

        /* Add btn */
        .rx-add-btn {
          width: 100%;
          height: 52px;
          border-radius: 16px;
          background: #0D9488;
          color: #fff;
          font-size: 14px;
          font-weight: 900;
          border: none;
          cursor: pointer;
          box-shadow: 0 6px 20px rgba(13,148,136,0.28);
          transition: background 0.18s, transform 0.15s;
          letter-spacing: 0.02em;
          display: block;
          margin-bottom: 10px;
        }
        .rx-add-btn:hover { background: #0F766E; transform: translateY(-1px); }
        .rx-add-btn--edit { background: #d97706; box-shadow: 0 6px 20px rgba(217,119,6,0.22); }
        .rx-add-btn--edit:hover { background: #b45309; }
        .rx-cancel-edit-btn {
          width: 100%;
          height: 40px;
          border-radius: 12px;
          background: #fff;
          border: 1.5px solid #E5E7EB;
          color: #9CA3AF;
          font-weight: 700;
          font-size: 13px;
          cursor: pointer;
          transition: border-color 0.18s;
          display: block;
        }
        .rx-cancel-edit-btn:hover { border-color: #d1d5db; }

        /* Right: Table card */
        .rx-right { display: flex; flex-direction: column; }
        .rx-table-card {
          background: #fff;
          border-radius: 20px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.05);
          overflow: hidden;
          flex: 1;
        }
        .rx-table-card-header {
          padding: 18px 24px;
          border-bottom: 1.5px solid #F3F4F6;
        }

        /* Table overrides */
        .rx-table .ant-table-thead > tr > th {
          background: transparent !important;
          font-weight: 900 !important;
          color: #CBD5E1 !important;
          text-transform: uppercase !important;
          font-size: 10px !important;
          letter-spacing: 0.12em !important;
          border-bottom: 2px solid #F8FAFC !important;
          padding: 14px 12px !important;
        }
        .rx-table .ant-table-tbody > tr > td {
          padding: 14px 12px !important;
          border-bottom: 1px solid #F8FAFC !important;
          vertical-align: middle !important;
        }
        .rx-table .ant-table-tbody > tr:hover > td {
          background: #f0fdfa !important;
        }
        .rx-table .ant-table {
          border-radius: 0 !important;
        }

        /* Footer bar */
        .rx-footer-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          border-top: 1.5px solid #F3F4F6;
          background: #fff;
          border-radius: 0 0 20px 20px;
        }
        .rx-total-label {
          font-size: 9.5px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.18em;
          color: #9CA3AF;
          margin: 0 0 4px;
        }
        .rx-total-value {
          font-size: 32px;
          font-weight: 900;
          color: #111827;
          line-height: 1;
        }
        .rx-btn-cancel {
          height: 48px;
          padding: 0 32px;
          border-radius: 999px;
          border: 1.5px solid #E5E7EB;
          background: #fff;
          color: #111827;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: border-color 0.18s;
          font-family: 'Outfit', sans-serif;
        }
        .rx-btn-cancel:hover { border-color: #d1d5db; }
        .rx-btn-send {
          height: 48px;
          padding: 0 36px;
          border-radius: 999px;
          border: none;
          background: #0D9488;
          color: #fff;
          font-size: 14px;
          font-weight: 800;
          cursor: pointer;
          box-shadow: 0 4px 16px rgba(13,148,136,0.28);
          transition: background 0.18s, transform 0.15s;
          display: flex;
          align-items: center;
          font-family: 'Outfit', sans-serif;
        }
        .rx-btn-send:hover { background: #0F766E; transform: translateY(-1px); }
        .rx-btn-send:disabled { opacity: 0.7; cursor: not-allowed; transform: none; }
      `}</style>
    </div>
  );
};

export default Prescription;